import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'

import { RootState } from '../../redux/store'
import localizeDate from '../../../common/utils/localizeDate'
import { CheckMarkIcon } from '../../../common/icons/checkmark-icon'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import {
    useReportGetReportsForAdminQuery,
    useCompanyGetReportDeliverySchedulesQuery,
    useCompanyDeleteReportDeliveryScheduleMutation,
    ReportDeliverySchedule,
} from '../../../common/redux/api/web-api'
import { WidgetLayout } from '../../../lib/widget-layout'
import useText from '../../../common/hooks/useText'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { Table } from '../../../common/uikit/Table'
import Text from '../../../ui/Text'

import { AutomaticReportDeliveryDialog } from './dialogs'

export const AutomaticReportDeliveriesTable = () => {
    const text = useText()
    const { selectedCompanyId } = useSelector((state: RootState) => state.user)

    const [automaticReportDeliveryDialogIsOpen, setAutomaticReportDeliveryDialogIsOpen] = useState(false)
    const [selectedReportDeliverySchedule, setSelectedReportDeliverySchedule] = useState<ReportDeliverySchedule>()

    const { data: reports, isLoading } = useReportGetReportsForAdminQuery({
        selectedCompanyId,
    })
    const reportNames = useMemo(() => new Map(reports?.map((i) => [i.id, i.name])), [reports])

    const { data: reportDeliverySchedules } = useCompanyGetReportDeliverySchedulesQuery({
        companyId: selectedCompanyId,
    })

    const [deleteDeliveryScheduleRequest] = useCompanyDeleteReportDeliveryScheduleMutation()

    const openNewReportDeliveryDialog = () => {
        setSelectedReportDeliverySchedule(undefined)
        setAutomaticReportDeliveryDialogIsOpen(true)
    }

    const openEditReportDeliveryDialog = (reportDeliverySchedule: ReportDeliverySchedule) => {
        setSelectedReportDeliverySchedule(reportDeliverySchedule)
        setAutomaticReportDeliveryDialogIsOpen(true)
    }

    const hideReportDeliveryDialog = () => {
        setAutomaticReportDeliveryDialogIsOpen(false)
        setSelectedReportDeliverySchedule(undefined)
    }

    const handleDeleteDeliverySchedule = async (reportDeliverySchedule: ReportDeliverySchedule) => {
        if (!reportDeliverySchedule || !reportDeliverySchedule.id) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: (
                <Text
                    code="msgConfirmReportRemoving"
                    values={{ reportName: reportNames.get(reportDeliverySchedule.reportId) }}
                />
            ),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await deleteDeliveryScheduleRequest({
                companyId: selectedCompanyId,
                reportDeliveryScheduleId: reportDeliverySchedule.id,
            }).unwrap()
            notifySuccess('Report delivery schedule has been removed')
        } catch (_) {
            notifyError('Failed to remove report delivery schedule')
        }
    }
    return (
        <>
            <WidgetLayout
                title={text('lblAutomaticReportDeliveriesField')}
                headerButtons={
                    <Button
                        type="button"
                        icon="pi pi-plus"
                        label={text('lblAdd')}
                        onClick={openNewReportDeliveryDialog}
                    />
                }
            >
                <Table
                    value={reportDeliverySchedules ?? []}
                    loading={isLoading}
                    dataKey="id"
                    pt={{ table: { 'aria-label': text('lblAutomaticReportDeliveriesField') } }}
                    data-testid="automatic-report-deliveries-table"
                >
                    <Column
                        field="active"
                        header={text('lblActive')}
                        bodyClassName="w-1rem"
                        body={(reportDeliverySchedule: ReportDeliverySchedule) => (
                            <div className="flex justify-content-center">
                                {reportDeliverySchedule.active ? (
                                    <CheckMarkIcon size={18} />
                                ) : (
                                    <i
                                        className="pi pi-times text-red-500"
                                        aria-label={text('lblInactiveStatus')}
                                        title={text('lblInactiveStatus')}
                                    />
                                )}
                            </div>
                        )}
                    />
                    <Column
                        field="reportId"
                        header={text('lblReport')}
                        body={(reportDeliverySchedule: ReportDeliverySchedule) =>
                            reportDeliverySchedule.reportId && reportNames.get(reportDeliverySchedule.reportId)
                        }
                    />
                    <Column field="deliveryType" header={text('lblType')} />
                    <Column field="deliveryInterval" header={text('lblInterval')} />
                    <Column field="receiverEmail" header={text('lblReceiverEmail')} />
                    <Column
                        field="lastSent"
                        header={text('lblLastSent')}
                        body={(reportDeliverySchedule: ReportDeliverySchedule) =>
                            localizeDate(reportDeliverySchedule.lastSent)
                        }
                    />
                    <Column
                        bodyClassName="w-8rem text-right"
                        body={(reportDeliverySchedule: ReportDeliverySchedule) => (
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    type="button"
                                    icon="pi pi-pencil"
                                    size="small"
                                    severity="info"
                                    outlined
                                    rounded
                                    aria-label={text('btnEdit')}
                                    tooltip={text('btnEdit')}
                                    onClick={() => openEditReportDeliveryDialog(reportDeliverySchedule)}
                                />
                                <Button
                                    type="button"
                                    icon="pi pi-trash"
                                    size="small"
                                    severity="danger"
                                    outlined
                                    rounded
                                    aria-label={text('btnRemove')}
                                    tooltip={text('lblDelete')}
                                    onClick={() => handleDeleteDeliverySchedule(reportDeliverySchedule)}
                                />
                            </div>
                        )}
                    />
                </Table>
            </WidgetLayout>

            <AutomaticReportDeliveryDialog
                key={selectedReportDeliverySchedule?.id ?? 'new'}
                companyId={selectedCompanyId}
                reports={reports}
                visible={automaticReportDeliveryDialogIsOpen}
                onHide={hideReportDeliveryDialog}
                selectedReportDeliverySchedule={selectedReportDeliverySchedule}
            />
        </>
    )
}
